import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import Typhography from '../../../shared/Typhography/Typhography';
import styles from './ObjectivesRankChart.module.scss';
import type {
  ITechnicalObjectiveAggregate,
  ITacticalObjectiveAggregate,
} from '../../../models/trainingStatistics';

/**
 * Sezione 4/6 — Top 10 Obiettivi Tecnici o Tattici (ranked bar chart).
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 2841-2898.
 */
type ObjectiveData = ITechnicalObjectiveAggregate | ITacticalObjectiveAggregate;

interface ObjectivesRankChartProps {
  data: ObjectiveData[];
  objectiveLabels?: Record<string, string>;
  title: string;
  color?: string;
  /** Se passato, click su barra apre modal esterno invece di inline drill-down. */
  onObjectiveClick?: (objectiveId: string) => void;
}

const hasByMethod = (o: ObjectiveData): o is ITechnicalObjectiveAggregate =>
  'byMethod' in o;

const fmtMinutes = (m: number) => {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

const ObjectivesRankChart: React.FC<ObjectivesRankChartProps> = ({
  data,
  objectiveLabels = {},
  title,
  color = '#063444',
  onObjectiveClick,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const safeData = Array.isArray(data) ? data : [];
  if (safeData.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Typhography size="regular-14" className={styles.emptyText}>
          Nessun dato obiettivo disponibile.
        </Typhography>
      </div>
    );
  }

  const sorted = [...safeData].sort((a, b) => b.minutes - a.minutes);
  const visible = showAll ? sorted : sorted.slice(0, 10);

  const chartData = visible.map((d) => ({
    name: objectiveLabels[d.objectiveId] ?? d.objectiveId,
    id: d.objectiveId,
    minutes: d.minutes,
    percentage: d.percentage,
  }));

  const selected = selectedId ? sorted.find((d) => d.objectiveId === selectedId) : null;

  return (
    <div className={styles.container}>
      <Typhography size="bold-14" className={styles.title}>{title}</Typhography>

      <ResponsiveContainer width="100%" height={Math.max(180, visible.length * 44)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 4, right: 80, bottom: 4, left: 140 }}
          onClick={(e) => {
            const idx = typeof e?.activeTooltipIndex === 'number' ? e.activeTooltipIndex : undefined;
            const id = idx !== undefined ? chartData[idx]?.id : undefined;
            if (!id) return;
            if (onObjectiveClick) {
              onObjectiveClick(id);
              return;
            }
            setSelectedId(id === selectedId ? null : id);
          }}
        >
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 12, fill: '#00212c', fontFamily: 'Satoshi, sans-serif' }}
            width={130}
          />
          <Tooltip
            formatter={(value) => [fmtMinutes(Number(value)), 'Tempo totale']}
          />
          <Bar dataKey="minutes" radius={[0, 6, 6, 0]}>
            {chartData.map((entry) => (
              <Cell
                key={entry.id}
                fill={entry.id === selectedId ? '#ffd15a' : color}
                cursor="pointer"
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Drill-down panel for selected objective */}
      {selected && hasByMethod(selected) && selected.byMethod && (
        <div className={styles.drillDown}>
          <Typhography size="bold-14" className={styles.drillTitle}>
            {objectiveLabels[selected.objectiveId] ?? selected.objectiveId} — Breakdown per metodo
          </Typhography>
          <div className={styles.methodBreakdown}>
            {Object.entries(selected.byMethod)
              .filter(([, v]) => v > 0)
              .sort(([, a], [, b]) => b - a)
              .map(([method, mins]) => (
                <div key={method} className={styles.methodRow}>
                  <span className={styles.methodName}>{method}</span>
                  <div className={styles.methodBar}>
                    <div
                      className={styles.methodBarFill}
                      style={{
                        width: `${Math.min(100, (mins / selected.minutes) * 100)}%`,
                        background: color,
                      }}
                    />
                  </div>
                  <span className={styles.methodMinutes}>{fmtMinutes(mins)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {sorted.length > 10 && (
        <button className={styles.showMoreBtn} onClick={() => setShowAll((p) => !p)}>
          {showAll ? (
            <>
              <CaretUp size={14} />
              Mostra meno
            </>
          ) : (
            <>
              <CaretDown size={14} />
              Mostra tutti ({sorted.length})
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ObjectivesRankChart;
